import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { maskitoParseNumber } from '@maskito/kit';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { PredefinedInputMaskOptions } from '@blaze-shared/shared-ui';
import {
  DEFAULT_NUMBER_PRECISION,
  isNullish,
  isNullishOrEmptyString,
} from '@blaze-shared/shared-util';

import { type InlineEditorConfig } from '../base-editor/inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'be-percent-editor',
  templateUrl: './percent-editor.component.html',
  styleUrls: ['./percent-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TablePercentEditorComponent implements OnInit, AfterViewInit {
  valueChange = output<string | number | null>();

  @Input() config?: InlineEditorConfig;

  @ViewChild('stringInput') stringInput!: ElementRef<HTMLInputElement>;

  formControl = new FormControl<number | null>(null);
  percentFormControl = new FormControl('', { nonNullable: true });
  maskOptions: PredefinedInputMaskOptions | null = null;

  @Input() set value(val: string | number | undefined) {
    if (isNullish(val) || typeof val === 'string') {
      return;
    }
    const precision = val.toString().includes('.') ? val.toString().split('.')[1].length - 2 : 0;
    this.percentFormControl.patchValue((val * 100).toFixed(precision < 0 ? 0 : precision));
  }

  constructor() {
    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.emitValue(value);
    });

    this.percentFormControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (isNullishOrEmptyString(value.replaceAll('%', ''))) {
        this.emitValue(null);
        return;
      }
      let parsed: number = maskitoParseNumber(value);
      if (isNaN(parsed)) {
        parsed = 0;
      }

      if (parsed) {
        const precision = parsed.toString().includes('.')
          ? parsed.toString().split('.')[1].length
          : 0;
        this.formControl.patchValue(Number((parsed / 100).toPrecision(precision + 2)));
      } else {
        this.formControl.patchValue(parsed);
      }
    });
  }

  ngOnInit(): void {
    const thousandSeparator =
      new Intl.NumberFormat().formatToParts(1000).find((part) => part.type === 'group')?.value ??
      '';
    const precision: number = Number.isInteger(this.config?.columnSchema?.precision)
      ? (this.config?.columnSchema?.precision ?? DEFAULT_NUMBER_PRECISION)
      : DEFAULT_NUMBER_PRECISION;

    this.maskOptions = {
      numberFormat: {
        format: 'percent',
        thousandSeparator,
        precision,
      },
    };
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.stringInput.nativeElement.focus();
      this.stringInput.nativeElement.select();
    });
  }

  private emitValue(value: string | number | null): void {
    this.valueChange.emit(value);
  }
}
